import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, CircularProgress, Dialog, DialogActions,
         DialogContent, DialogTitle, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import { refreshUserUser, selectUser } from "../../../Store/userSlice";
import ReservationService from "../../../api/ReservationService";
import { toast } from "react-toastify";
import { FaCoins, FaMoneyBillWave } from "react-icons/fa";
import "../ReservationV2/BookingPage.css";
import "./ReservationDetailPage.css";

const MONTHS_FR = [
  "janvier","février","mars","avril","mai","juin",
  "juillet","août","septembre","octobre","novembre","décembre",
];
const DAYS_FR = ["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"];

function getInitials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";
}

function fmtTime(iso) {
  return iso?.split("T")[1]?.substring(0, 5) ?? "";
}
function fmtDateFull(iso) {
  const d = new Date(iso);
  return `${DAYS_FR[d.getDay()]} ${d.getDate()} ${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
}

export default function ReservationDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user     = useSelector(selectUser);

  const [reservation, setReservation] = useState(null);
  const [fetching,    setFetching]    = useState(true);
  const [error,       setError]       = useState(null);
  const [copied,      setCopied]      = useState(false);
  const [cancelling,  setCancelling]  = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);  // confirm dialog
  const [cancelWhat,  setCancelWhat]  = useState(null);   // "booking" | "full"

  /* ── Fetch detail ─────────────────────────────────────── */
  const loadDetail = () => {
    setFetching(true);
    ReservationService.getReservationDetail(id)
      .then((r) => {
        if (!r.data.isInReservation) {
          setError("Vous ne faites pas partie de cette réservation.");
        } else {
          setReservation(r.data);
        }
      })
      .catch((e) => {
        const msg = e?.response?.data?.message;
        setError(msg || "Impossible de charger cette réservation.");
      })
      .finally(() => setFetching(false));
  };

  useEffect(() => { if (id) loadDetail(); }, [id]);

  /* ── Cancellation timing ──────────────────────────────── */
  const canCancel = reservation
    ? new Date(reservation.start).getTime() > Date.now() + 24 * 3600 * 1000
    : false;

  /* ── Share link ──────────────────────────────────────── */
  const shareLink = `${window.location.origin}/reservation/join/${id}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(shareLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  /* ── Cancel ──────────────────────────────────────────── */
  const openCancel = (what) => {
    if (!canCancel) return;
    setCancelWhat(what);
    setConfirmOpen(true);
  };

  const handleCancelConfirm = async () => {
    setConfirmOpen(false);
    setCancelling(true);
    try {
      const token = JSON.parse(localStorage.getItem("token")).accessToken;
      if (cancelWhat === "full") {
        await ReservationService.cancelReservation(token, { reservationId: id });
        toast.success("Réservation annulée.", { theme: "light" });
        dispatch(refreshUserUser());
        navigate("/my-reservation");
      } else {
        // cancel my booking only — determine what they paid
        const myEntry = reservation?.myBooking;
        if (myEntry?.nombreOfJetonsPayed > 0) {
          await ReservationService.cancelReservationBooking(token, {
            reservationId: id,
            cancel_type: "jetons",
          });
        }
        if (myEntry?.numberOfPlacesByCash > 0) {
          await ReservationService.cancelReservationBooking(token, {
            reservationId: id,
            cancel_type: "places",
          });
        }
        toast.success("Votre place a été annulée.", { theme: "light" });
        dispatch(refreshUserUser());
        navigate("/my-reservation");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Erreur lors de l'annulation.", {
        theme: "light",
      });
    }
    setCancelling(false);
  };

  /* ── Loading ─────────────────────────────────────────── */
  if (fetching) {
    return (
      <div className="phone-page" style={{ justifyContent: "center", alignItems: "center" }}>
        <CircularProgress sx={{ color: "#1a5689" }} />
      </div>
    );
  }

  /* ── Error ────────────────────────────────────────────── */
  if (error || !reservation) {
    return (
      <div className="phone-page">
        <div className="phone-header">
          <IconButton className="phone-header-back" onClick={() => navigate(-1)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <div className="phone-header-titles">
            <div className="phone-header-title">Détail</div>
          </div>
        </div>
        <div className="rd-centered">
          <div className="rd-error-emoji">⚠️</div>
          <div className="rd-error-text">{error || "Réservation introuvable."}</div>
          <Button variant="contained" onClick={() => navigate("/my-reservation")}
            sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, mt: 1 }}>
            Retour
          </Button>
        </div>
      </div>
    );
  }

  const occupied    = 4 - (reservation.availablePlaces ?? 4);
  const isFuture    = new Date(reservation.start) > new Date();
  const canShare    = isFuture && reservation.availablePlaces > 0;
  const myEntry     = reservation.myBooking;
  const isCreator   = reservation.isCreator;

  return (
    <div className="phone-page">
      {/* Header */}
      <div className="phone-header">
        <IconButton className="phone-header-back" onClick={() => navigate("/my-reservation")} size="small">
          <ArrowBackIcon />
        </IconButton>
        <div className="phone-header-titles">
          <div className="phone-header-title">Ma réservation</div>
          <div className="phone-header-sub">{reservation.terrain?.name}</div>
        </div>
      </div>

      <div className="phone-body">

        {/* Top banner */}
        <div className="rd-banner">
          <div className="rd-banner-court">{reservation.terrain?.name}</div>
          <div className="rd-banner-time">
            {fmtTime(reservation.start)} – {fmtTime(reservation.end)}
          </div>
          <div className="rd-banner-date">{fmtDateFull(reservation.start)}</div>
          <div className={`rd-banner-status ${reservation.reservationConfirmed ? "rd-banner-status--ok" : ""}`}>
            {reservation.reservationConfirmed ? "✓ Confirmée" : "⏳ En attente de confirmation"}
          </div>
        </div>

        {/* My booking */}
        {myEntry && (
          <div className="rd-card">
            <div className="rd-card-label">Mon booking</div>
            {myEntry.nombreOfJetonsPayed > 0 && (
              <div className="rd-booking-row">
                <span className="rd-booking-icon"><FaCoins style={{ color: "#f59e0b" }} /></span>
                <span className="rd-booking-label">Jetons payés</span>
                <span className="rd-booking-value">{myEntry.nombreOfJetonsPayed}</span>
              </div>
            )}
            {myEntry.numberOfPlacesByCash > 0 && (
              <div className="rd-booking-row">
                <span className="rd-booking-icon"><FaMoneyBillWave style={{ color: "#16a34a" }} /></span>
                <span className="rd-booking-label">Places en espèces</span>
                <span className="rd-booking-value">{myEntry.numberOfPlacesByCash}</span>
              </div>
            )}
          </div>
        )}

        {/* Team */}
        <div className="rd-card">
          <div className="rd-card-label">Équipe</div>
          <div className="rd-team-row">
            {[0, 1, 2, 3].map((i) => {
              const player = reservation.players?.[i];
              const isMe = player?.isCurrentUser;
              return (
                <div key={i} className={`rd-bubble ${isMe ? "rd-bubble--you" : player ? "rd-bubble--taken" : "rd-bubble--free"}`}>
                  {player ? getInitials(player.userFullName) : "+"}
                </div>
              );
            })}
          </div>
          {reservation.players?.length > 0 && (
            <div className="join-player-names">
              {reservation.players.map((p, i) => (
                <div key={i} className="join-player-name-row">
                  <span className="join-player-dot" style={p.isCurrentUser ? { background: "#1a5689" } : {}} />
                  <span style={p.isCurrentUser ? { fontWeight: 700, color: "#1a5689" } : {}}>{p.userFullName}{p.isCurrentUser ? " (vous)" : ""}</span>
                  {i === 0 && <span className="join-player-creator-tag">organisateur</span>}
                </div>
              ))}
            </div>
          )}
          <div className="rd-team-desc">
            {occupied} joueur{occupied > 1 ? "s" : ""} sur 4 &nbsp;•&nbsp;
            {reservation.availablePlaces > 0
              ? `${reservation.availablePlaces} place${reservation.availablePlaces > 1 ? "s" : ""} restante${reservation.availablePlaces > 1 ? "s" : ""}`
              : "Complet"}
          </div>
        </div>

        {/* Share */}
        {canShare && (
          <div className="rd-card">
            <div className="rd-card-label">Inviter des partenaires</div>
            <div className="rd-share-row" onClick={handleCopy}>
              <div className="rd-share-icon">🔗</div>
              <div className="rd-share-text">
                <div className="rd-share-title">Copier le lien d'invitation</div>
                <div className="rd-share-sub">Partagez avec vos partenaires de jeu</div>
              </div>
              <span className="rd-share-arrow">›</span>
            </div>
            {copied && <div className="rd-copied-label">✓ Lien copié !</div>}
          </div>
        )}

        {/* Cancel options */}
        {isFuture && (
          <div className="rd-card">
            <div className="rd-card-label">Annulation</div>

            {/* Creator: only cancel full reservation */}
            {isCreator && (
              <div
                className={`rd-cancel-row ${!canCancel ? "rd-cancel-disabled" : ""}`}
                onClick={() => openCancel("full")}
              >
                <div className="rd-cancel-icon">❌</div>
                <div>
                  <div className="rd-cancel-title">Annuler toute la réservation</div>
                  <div className="rd-cancel-sub">
                    {canCancel
                      ? "Tous les joueurs seront remboursés"
                      : "Annulation impossible (moins de 24h)"}
                  </div>
                </div>
              </div>
            )}

            {/* Non-creator player: cancel their own spot only */}
            {!isCreator && myEntry && (
              <div
                className={`rd-cancel-row ${!canCancel ? "rd-cancel-disabled" : ""}`}
                onClick={() => openCancel("booking")}
              >
                <div className="rd-cancel-icon">🚫</div>
                <div>
                  <div className="rd-cancel-title">Annuler ma place</div>
                  <div className="rd-cancel-sub">
                    {canCancel
                      ? "Votre place sera remboursée"
                      : "Annulation impossible (moins de 24h)"}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Confirm cancel dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: "18px", mx: 2, width: "100%", maxWidth: 400 } }}
      >
        <DialogTitle sx={{ pt: 3, px: 3, pb: 1, fontSize: 17, fontWeight: 700 }}>
          {cancelWhat === "full" ? "Annuler la réservation" : "Annuler ma place"}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1, color: "#64748b", fontSize: 14 }}>
          {cancelWhat === "full"
            ? "Êtes-vous sûr ? Toute la réservation sera supprimée et les places remboursées."
            : "Êtes-vous sûr ? Votre place sera annulée et remboursée."}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined"
            sx={{ borderRadius: "10px", flex: 1, textTransform: "none", fontWeight: 600 }}>
            Retour
          </Button>
          <Button onClick={handleCancelConfirm} variant="contained" color="error"
            disabled={cancelling}
            sx={{ borderRadius: "10px", flex: 1, textTransform: "none", fontWeight: 700 }}>
            {cancelling ? <CircularProgress size="1rem" sx={{ color: "white" }} /> : "Confirmer"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
